/**
 * Open Zoom for a meeting without dragging the user off the dashboard.
 *
 * The problem we're solving: clicking "Start" today opens Zoom's join page as a
 * full browser tab that grabs focus, so the user is yanked off the dashboard and
 * forgets Live Insights exists. But that join page is also what auto-launches the
 * Zoom *desktop app* — which is what we actually want, because the native app
 * doesn't compete with our browser insights view.
 *
 * So instead of a full tab, we open the join page in a tiny throwaway popup
 * tucked into the corner, over the dashboard, inside the click gesture (so it
 * isn't popup-blocked). It fires the desktop app, then we close it as soon as
 * the user is back on our tab — or after a short backstop delay — so it never
 * lingers as a stray window. Meanwhile the caller navigates the main tab to Live
 * Insights, so the desktop app and our insights end up side by side.
 *
 * Users WITHOUT the desktop app can't be auto-helped here: their meeting would
 * have to live in a browser surface, which we can't keep behind our insights
 * view. They fall back to the persistent "Join" link on the Live Insights page.
 *
 * @param {string} joinUrl  Full https Zoom join URL.
 * @param {object} [opts]
 * @param {number} [opts.backstopMs=2500]  Close the launcher by this point regardless.
 * @param {number} [opts.settleMs=800]     Don't let a refocus close it before this (lets the
 *                                          desktop hand-off complete first).
 * @param {number} [opts.size=160]         Popup edge length in px (browsers clamp small popups ~100).
 */
export function launchZoomMeeting(joinUrl, { backstopMs = 2500, settleMs = 800, size = 160 } = {}) {
  if (!joinUrl) return

  const w = Math.max(100, size)
  const h = Math.max(100, size)
  let left = 40
  const top = 40
  try {
    // Tuck it into the top-right corner so it's the least intrusive over the dashboard.
    const screenW = window.screen?.availWidth ?? window.innerWidth ?? 0
    if (screenW) left = Math.max(0, screenW - w - 40)
  } catch { /* ignore */ }

  let popup = null
  try {
    popup = window.open(joinUrl, 'rithmo_zoom_launch', `popup,width=${w},height=${h},left=${left},top=${top}`)
  } catch {
    popup = null
  }

  if (!popup) {
    // Popup blocked despite the gesture — fall back to a normal tab so the user
    // can still reach Zoom rather than being stranded.
    try { window.open(joinUrl, '_blank') } catch { /* ignore */ }
    return
  }

  let closed = false
  const onRefocus = () => {
    // Once the user is back on our tab (desktop app launched, or they switched
    // back), the launcher has done its job — drop it.
    if (document.visibilityState === 'visible' && document.hasFocus?.()) closePopup()
  }
  function closePopup() {
    if (closed) return
    closed = true
    window.removeEventListener('focus', onRefocus)
    document.removeEventListener('visibilitychange', onRefocus)
    try { if (popup && !popup.closed) popup.close() } catch { /* ignore */ }
  }

  // Only start watching for "user is back" after a settle window, so we don't
  // close the launcher before it's had a chance to fire the desktop protocol.
  window.setTimeout(() => {
    if (closed) return
    window.addEventListener('focus', onRefocus)
    document.addEventListener('visibilitychange', onRefocus)
    onRefocus()
  }, settleMs)

  // Backstop: close it regardless once the desktop hand-off has had time to land.
  window.setTimeout(closePopup, backstopMs)
}
